import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { ExpertApplicationData, availableSpecializations, availabilityOptions } from "./schema";

interface SpecializationsStepProps {
  form: UseFormReturn<ExpertApplicationData>;
  onNext: () => void;
  onPrev: () => void;
  selectedSpecializations: string[];
  onSpecializationChange: (specialization: string, checked: boolean) => void;
  selectedAvailability: string[];
  onAvailabilityChange: (availability: string, checked: boolean) => void;
}

export function SpecializationsStep({ 
  form, 
  onNext,
  onPrev, 
  selectedSpecializations,
  onSpecializationChange,
  selectedAvailability,
  onAvailabilityChange
}: SpecializationsStepProps) {
  const { register, formState: { errors } } = form;

  return (
    <div className="space-y-6">
      {/* Areas of Specialization */}
      <div>
        <Label>Areas of Specialization *</Label>
        <p className="text-sm text-gray-600 mb-3">Select all that apply - families will find you based on these specializations</p>
        <div className="grid md:grid-cols-2 gap-3">
          {availableSpecializations.map((specialization) => (
            <div key={specialization.id} className="flex items-start space-x-2 p-3 border rounded-lg hover:bg-gray-50">
              <Checkbox
                id={specialization.id}
                checked={selectedSpecializations.includes(specialization.id)}
                onCheckedChange={(checked) => 
                  onSpecializationChange(specialization.id, checked as boolean)
                }
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Label htmlFor={specialization.id} className="text-sm font-medium">
                    {specialization.label}
                  </Label>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="w-4 h-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-sm">{specialization.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Other Specialization */}
        <div className="mt-4">
          <Label htmlFor="otherSpecialization">Other Specialization</Label>
          <Input 
            id="otherSpecialization"
            {...register("otherSpecialization")}
            placeholder="Describe any other areas of expertise..."
          />
        </div>
        
        {errors.specializations && (
          <p className="text-red-600 text-sm mt-1">{errors.specializations.message}</p>
        )}
      </div>

      {/* Availability */}
      <div>
        <Label>Availability *</Label>
        <p className="text-sm text-gray-600 mb-3">When are you typically available for consultations?</p>
        <div className="grid md:grid-cols-2 gap-2">
          {availabilityOptions.map((option) => (
            <div key={option.id} className="flex items-center space-x-2">
              <Checkbox
                id={option.id}
                checked={selectedAvailability.includes(option.id)}
                onCheckedChange={(checked) => 
                  onAvailabilityChange(option.id, checked as boolean)
                }
              />
              <Label htmlFor={option.id} className="text-sm">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
        {errors.availability && (
          <p className="text-red-600 text-sm mt-1">{errors.availability.message}</p>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onPrev}>
          Back
        </Button>
        <Button type="button" onClick={onNext} className="bg-grandpal-green hover:bg-grandpal-green/90">
          Continue to Experience
        </Button>
      </div>
    </div>
  );
} 